import React, { useState, useEffect, useCallback } from 'react';
import {
  Sparkles, Download, Zap, Trash2, Loader2, Info, ShieldCheck,
  TrendingUp, Users, DollarSign, BookOpen, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';
import DataCompletenessMeter from '../components/benchmark/DataCompletenessMeter';

const theme = getLightSectionTheme('data');

const ModeledBadge = () => (
  <Badge className="bg-cyan-50 text-cyan-700 border border-cyan-200 gap-1" data-testid="modeled-badge">
    <Sparkles className="h-3 w-3" /> Modeled
  </Badge>
);

function RangeRow({ label, cell, fmt = (v) => v }) {
  if (!cell) return null;
  return (
    <tr className="border-b border-slate-100">
      <td className="py-2 pr-3 text-slate-700">{label}</td>
      <td className="py-2 px-3 font-semibold text-slate-900">{fmt(cell.value)}</td>
      <td className="py-2 px-3 text-slate-500 whitespace-nowrap">{fmt(cell.low)} – {fmt(cell.high)}</td>
      <td className="py-2 pl-3"><span className="text-xs text-slate-400">{cell.source}</span></td>
    </tr>
  );
}

export default function BenchmarkBaselinePage() {
  const [options, setOptions] = useState({ industries: [], size_bands: [] });
  const [industry, setIndustry] = useState('');
  const [headcount, setHeadcount] = useState('');
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [selectedStreams, setSelectedStreams] = useState(null); // null until initialized
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const [{ data }, comp] = await Promise.all([
        api.get('/admin/benchmark-baseline/status'),
        api.get('/admin/benchmark-baseline/completeness').catch(() => null),
      ]);
      setStatus(data);
      if (comp?.data) {
        setCompleteness(comp.data);
        // Default selection: pre-check only streams not already measured.
        setSelectedStreams((prev) => prev ?? Object.fromEntries(
          comp.data.streams.map((s) => [s.id, s.status !== 'measured'])));
      }
      const meta = data.applied_meta;
      if (meta) {
        setIndustry((prev) => prev || meta.industry);
        setHeadcount((prev) => prev || String(meta.headcount || ''));
      }
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/benchmark-baseline/options');
        setOptions(data);
      } catch (e) {
        toast.error('Failed to load benchmark options');
      }
      loadStatus();
    })();
  }, [loadStatus]);

  const runPreview = async () => {
    if (!industry) { toast.error('Pick an industry first'); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ industry });
      if (headcount) params.set('headcount', headcount);
      const { data } = await api.get(`/admin/benchmark-baseline/preview?${params}`);
      setPreview(data);
    } catch (e) {
      toast.error('Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const streams = selectedStreams
        ? Object.keys(selectedStreams).filter((k) => selectedStreams[k])
        : undefined;
      const { data } = await api.post('/admin/benchmark-baseline/apply', {
        industry, headcount: headcount ? Number(headcount) : undefined,
        streams,
      });
      toast.success(`Applied ${data.applied_streams?.length || 0} modeled stream(s) — Overall LEI ${data.overall_lei}`);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const toggleStream = (id) =>
    setSelectedStreams((prev) => ({ ...(prev || {}), [id]: !(prev || {})[id] }));
  const selectedCount = selectedStreams
    ? Object.values(selectedStreams).filter(Boolean).length : 0;
  const conflictStreams = (completeness?.streams || []).filter(
    (s) => s.status === 'measured' && selectedStreams?.[s.id]);

  const clearData = async () => {
    try {
      await api.delete('/admin/benchmark-baseline/clear');
      toast.success('Modeled data removed');
      loadStatus();
    } catch (e) {
      toast.error('Clear failed');
    }
  };

  const downloadAppendix = async () => {
    if (!industry) { toast.error('Pick an industry first'); return; }
    try {
      const params = new URLSearchParams({ industry });
      if (headcount) params.set('headcount', headcount);
      const res = await api.get(`/admin/benchmark-baseline/appendix.pdf?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Benchmark_Methodology_Appendix_${industry}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Appendix download failed');
    }
  };

  const totalModeled = status
    ? Object.values(status.collections).reduce((s, c) => s + c.modeled, 0) : 0;
  const pe = preview?.people_engine;

  return (
    <PageShell theme={theme} hero={(
      <PageHero
        title="Benchmark Baseline"
        description="No LEI, capability, or KPI data yet? Synthesize a defensible modeled baseline from published industry × size benchmarks so the full value chain runs — then replace it with your own data over time."
        icon={Sparkles}
        theme={theme}
        testId="benchmark-baseline-hero"
      />
    )}>
      <div className="space-y-6" data-testid="benchmark-baseline-page">
        {/* Configure */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-cyan-600" /> Configure baseline
            </CardTitle>
            <CardDescription>
              Pick the organization's industry and size. Values are computed as
              <code className="mx-1 px-1.5 py-0.5 bg-slate-100 rounded text-cyan-700 text-xs">global benchmark × industry × size</code>
              with a citable source and confidence range on every number.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Industry</label>
                <Select value={industry} onValueChange={setIndustry}>
                  <SelectTrigger data-testid="industry-select"><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>
                    {options.industries.map((i) => (
                      <SelectItem key={i.id} value={i.id} data-testid={`industry-opt-${i.id}`}>{i.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Headcount</label>
                <Input type="number" min="1" placeholder="e.g. 2500" value={headcount}
                  onChange={(e) => setHeadcount(e.target.value)} data-testid="headcount-input" />
              </div>
              <div className="flex items-end">
                <Button onClick={runPreview} disabled={loading} className="w-full gap-2" data-testid="preview-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
                  Preview modeled baseline
                </Button>
              </div>
            </div>
            {status?.applied_meta && (
              <Alert className="bg-cyan-50 border-cyan-200" data-testid="applied-status">
                <Info className="h-4 w-4 text-cyan-600" />
                <AlertDescription className="text-cyan-800 text-sm">
                  A modeled baseline is currently applied — <strong>{status.applied_meta.industry_label}</strong>,
                  {' '}{status.applied_meta.size_band_label} ({totalModeled} modeled rows).
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Data completeness meter */}
        {completeness && (
          <Card data-testid="completeness-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-600" /> Data completeness
              </CardTitle>
              <CardDescription>
                How much of the value chain runs on your own measured data vs modeled benchmarks.
                Every stream you replace makes the numbers easier to defend.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataCompletenessMeter completeness={completeness} />
            </CardContent>
          </Card>
        )}

        {/* Preview */}
        {preview && (
          <Card data-testid="preview-panel">
            <CardHeader>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    {preview.industry_label} · {preview.size_band_label} <ModeledBadge />
                  </CardTitle>
                  <CardDescription className="mt-1">{preview.confidence.note}</CardDescription>
                </div>
                <div className="flex gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-cyan-700" data-testid="preview-lei">{preview.lei.overall}</div>
                    <div className="text-xs text-slate-500">Overall LEI</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-indigo-700" data-testid="preview-pe">{pe.pe_composite}</div>
                    <div className="text-xs text-slate-500">People Engine</div>
                  </div>
                  <div className="text-center">
                    <Badge variant="outline" className="mt-2">{preview.confidence.level} confidence</Badge>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="lei">
                <TabsList>
                  <TabsTrigger value="lei" className="gap-1.5"><TrendingUp className="h-4 w-4" />LEI Measures</TabsTrigger>
                  <TabsTrigger value="pe" className="gap-1.5"><Users className="h-4 w-4" />People Engine</TabsTrigger>
                  <TabsTrigger value="kpi" className="gap-1.5"><DollarSign className="h-4 w-4" />KPI Baselines</TabsTrigger>
                </TabsList>

                <TabsContent value="lei" className="mt-4">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-200">
                      <th className="py-2 pr-3">Measure</th><th className="py-2 px-3">Modeled</th>
                      <th className="py-2 px-3">Range</th><th className="py-2 pl-3">Source</th></tr></thead>
                    <tbody>
                      {Object.values(preview.lei.measures).map((c) => (
                        <RangeRow key={c.name} label={c.name} cell={c} />
                      ))}
                    </tbody>
                  </table>
                </TabsContent>

                <TabsContent value="pe" className="mt-4">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-200">
                      <th className="py-2 pr-3">Input</th><th className="py-2 px-3">Modeled</th>
                      <th className="py-2 px-3">Range</th><th className="py-2 pl-3">Source</th></tr></thead>
                    <tbody>
                      <RangeRow label="Capability (C)" cell={pe.capability} />
                      <RangeRow label="Motivation (M)" cell={pe.motivation} />
                      <RangeRow label="Retention (R)" cell={pe.retention} />
                      <RangeRow label="KPI conversion" cell={pe.kpi_conversion} />
                      <RangeRow label="KPI improvement %" cell={pe.kpi_improvement_pct} fmt={(v) => `${v}%`} />
                    </tbody>
                  </table>
                </TabsContent>

                <TabsContent value="kpi" className="mt-4">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-200">
                      <th className="py-2 pr-3">KPI</th><th className="py-2 px-3">Global $/emp</th>
                      <th className="py-2 px-3">Modifier</th><th className="py-2 pl-3">Modeled $/emp</th></tr></thead>
                    <tbody>
                      {preview.kpi_baselines.map((k) => (
                        <tr key={k.kpi_id} className="border-b border-slate-100">
                          <td className="py-2 pr-3 text-slate-700">{k.name}</td>
                          <td className="py-2 px-3 text-slate-500">${k.global_baseline_per_employee.toLocaleString()}</td>
                          <td className="py-2 px-3 text-slate-500">×{k.size_industry_modifier}</td>
                          <td className="py-2 pl-3 font-semibold text-slate-900">${k.modeled_baseline_per_employee.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TabsContent>
              </Tabs>

              {/* Selective apply — choose which streams to model */}
              {completeness && selectedStreams && (
                <div className="mt-6 pt-4 border-t border-slate-100" data-testid="stream-selector">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-slate-800">Choose which streams to model</div>
                    <span className="text-xs text-slate-500">{selectedCount} of {completeness.streams.length} selected</span>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">
                    Only the streams you tick are written as modeled data. Streams you've already
                    <span className="text-emerald-600 font-medium"> measured</span> are left unticked so your real data stays intact.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {completeness.streams.map((s) => {
                      const isMeasured = s.status === 'measured';
                      const conflict = isMeasured && !!selectedStreams[s.id];
                      return (
                        <label key={s.id}
                          className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                            conflict ? 'border-amber-300 bg-amber-50'
                              : selectedStreams[s.id] ? 'border-cyan-300 bg-cyan-50/50' : 'border-slate-200 bg-white'}`}
                          data-testid={`stream-toggle-${s.id}`}>
                          <Checkbox checked={!!selectedStreams[s.id]} onCheckedChange={() => toggleStream(s.id)}
                            className="mt-0.5" data-testid={`stream-checkbox-${s.id}`} />
                          <div className="min-w-0">
                            <div className="text-xs font-medium text-slate-700">{s.label}</div>
                            <div className={`text-[10px] ${conflict ? 'text-amber-700 font-medium' : isMeasured ? 'text-emerald-600' : s.status === 'modeled' ? 'text-cyan-600' : 'text-slate-400'}`}>
                              {conflict ? (
                                <span className="inline-flex items-center gap-1">
                                  <AlertTriangle className="h-3 w-3" /> Will mix modeled data into a measured stream
                                </span>
                              ) : isMeasured ? 'Currently measured — leave unticked to keep your data'
                                : s.status === 'modeled' ? 'Currently modeled' : 'No data yet'}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {conflictStreams.length > 0 && (
                    <Alert className="mt-3 bg-amber-50 border-amber-200" data-testid="stream-conflict-warning">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800 text-xs">
                        You've selected {conflictStreams.length} stream{conflictStreams.length === 1 ? '' : 's'} you've
                        already <strong>measured</strong> ({conflictStreams.map((s) => s.label).join(', ')}). Applying will
                        mix modeled data into your real data for {conflictStreams.length === 1 ? 'it' : 'them'}. Untick to keep your measured data pristine.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t border-slate-100">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button className="gap-2" disabled={applying || selectedCount === 0} data-testid="apply-btn">
                      {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                      Apply {selectedCount > 0 ? `${selectedCount} ` : ''}modeled stream{selectedCount === 1 ? '' : 's'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Apply modeled baseline?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This writes <strong>only the {selectedCount} stream{selectedCount === 1 ? '' : 's'} you selected</strong> as
                        modeled data. Any stream you did <em>not</em> select keeps its current data untouched (measured data is never
                        overwritten). Modeled data replaces prior modeled data for the selected streams and can be cleared any time;
                        real data you import later overrides it.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    {conflictStreams.length > 0 && (
                      <div className="rounded-md bg-amber-50 border border-amber-200 p-3 flex items-start gap-2" data-testid="apply-conflict-callout">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-800">
                          Heads up: {conflictStreams.map((s) => s.label).join(', ')} {conflictStreams.length === 1 ? 'is' : 'are'} already
                          measured. Applying will <strong>mix modeled data into your real data</strong> for {conflictStreams.length === 1 ? 'it' : 'them'}.
                        </p>
                      </div>
                    )}
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={apply} data-testid="apply-confirm-btn">Apply</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <Button variant="outline" onClick={downloadAppendix} className="gap-2" data-testid="appendix-btn">
                  <Download className="h-4 w-4" /> Methodology appendix (PDF)
                </Button>

                {totalModeled > 0 && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" className="gap-2 text-rose-600 hover:text-rose-700" data-testid="clear-btn">
                        <Trash2 className="h-4 w-4" /> Clear modeled data
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove all modeled data?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This deletes every row tagged <strong>modeled</strong> for this organization
                          and clears the size-scaled KPI baseline override. Your imported (measured)
                          data is not touched.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={clearData} className="bg-rose-600 hover:bg-rose-700" data-testid="clear-confirm-btn">Clear</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>

              <div className="mt-4 flex items-start gap-2 text-xs text-slate-400">
                <BookOpen className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>Sources: {preview.sources.map((s) => s.label).join(' · ')}. Every value is defensible via the downloadable methodology appendix.</span>
              </div>
            </CardContent>
          </Card>
        )}

        {!preview && (
          <Alert data-testid="empty-hint">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Select an industry and headcount, then preview the modeled baseline. Nothing is written until you click <strong>Apply</strong>.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </PageShell>
  );
}
